import type { NetworkId, NetworkInfo } from "@alch/dx-entities";
import { COMPOSER_API_KEY, TRACE_COMPOSER_API_KEY } from "@util/constants";
import { IS_PRODUCTION } from "@util/deployEnv";
import { stringify } from "qs";
import type { ComposerMethodConfig } from "./methods.js";
import { getUniqueMethodConfig } from "./methods.js";
import { normalizeParamName } from "./params.js";
import type { SerializableComposerFormState } from "./sharing.js";
import { buildShareUrl, parseStateFromShareUrl } from "./sharing.js";
import { nullifyEmptyValues } from "./utils.js";

export const getComposerUrlFromRequest = <Request extends { method?: string }>({
  methodConfigs,
  baseUrl,
  network,
  request,
}: {
  methodConfigs: ComposerMethodConfig[];
  baseUrl: string;
  network: NetworkId;
  request: Request;
}) => {
  const methodConfig = getUniqueMethodConfig(methodConfigs, {
    method: String(request.method),
    network,
  });

  if (!methodConfig) return null;

  const shareState = {
    network,
    method: methodConfig.name,
    body: request,
  } as SerializableComposerFormState;
  const shareUrl = buildShareUrl({ baseUrl, state: shareState });
  const shareUrlQuery = shareUrl.split("?")[1] || "";

  const state = parseStateFromShareUrl({
    search: shareUrlQuery,
    methodConfigs,
  });

  if (!state) return null;

  return shareUrl;
};

/**
 * Restores original parameter names from the spec (which may include brackets)
 * by mapping normalized form field names back to their original names.
 */
const restoreOriginalParamNames = (
  query: Record<string, unknown>,
  methodConfig: ComposerMethodConfig,
): Record<string, unknown> => {
  if (!methodConfig.queryParams) {
    return query;
  }

  const paramNameMap = new Map(
    methodConfig.queryParams.map((param) => [
      normalizeParamName(param.name || ""),
      param.name || "",
    ]),
  );

  return Object.fromEntries(
    Object.entries(query).map(([key, value]) => [
      paramNameMap.get(key) || key,
      value,
    ]),
  );
};

const buildQueryString = (
  query: Record<string, unknown> | undefined,
  methodConfig: ComposerMethodConfig,
): string => {
  if (!query) return "";

  const restoredParams = restoreOriginalParamNames(query, methodConfig);
  const nullifiedParams = nullifyEmptyValues(restoredParams);

  return stringify(nullifiedParams, {
    addQueryPrefix: true,
    skipNulls: true,
    arrayFormat: "repeat",
  });
};

export const buildComposerRequestUrl = ({
  methodConfig,
  query,
  networkInfo,
  apiKey,
}: {
  methodConfig: ComposerMethodConfig;
  query: unknown | undefined;
  networkInfo: NetworkInfo | undefined;
  apiKey: string | undefined;
}) => {
  let url =
    networkInfo && "rpcUrl" in networkInfo // A temporary hack to use the rpcUrl for the request for V_DEVNET
      ? `${String(networkInfo.rpcUrl)}/{apiKey}`
      : methodConfig.url;

  const isTraceMethod = methodConfig.name.includes("trace");
  const defaultApiKey = isTraceMethod
    ? TRACE_COMPOSER_API_KEY
    : COMPOSER_API_KEY;

  url = url.replace("{apiKey}", apiKey || defaultApiKey);

  if (networkInfo) {
    url = url.replace("{network}", networkInfo.kebabCaseId);
  }

  if (!IS_PRODUCTION && !url.includes(defaultApiKey)) {
    url = url.replace("alchemy.com", "alchemypreview.com");
  }

  const queryString = buildQueryString(
    query as Record<string, unknown>,
    methodConfig,
  );

  return `${url}${queryString}`;
};

export const buildComposerRequestBody = ({
  methodConfig,
  body,
}: {
  methodConfig: ComposerMethodConfig;
  body: unknown | undefined;
}) => {
  if (!methodConfig.requestBody) {
    return "";
  }

  return JSON.stringify(nullifyEmptyValues(body));
};
